import { apiClient } from './apiClient';

export type LeadImportStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'duplicate'
  | 'imported'
  | 'failed';

export interface LeadImportBatch {
  id: string;
  name: string;
  source_type: string;
  status: string;
  total_records: number;
  accepted_count: number;
  rejected_count: number;
  duplicate_count: number;
  failed_count: number;
  created_at: string;
  updated_at: string;
}

export interface LeadImportRecord {
  id: string;
  batch: string;
  raw_data: Record<string, unknown>;
  normalized_data: Record<string, unknown>;
  import_status: LeadImportStatus;
  duplicate_reason: string;
  validation_errors: string[];
  matched_client: string | null;
  imported_client: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadImportCreatePayload {
  batch_name: string;
  source_type: string;
  leads: Record<string, unknown>[];
}

export interface LeadImportParsePayload {
  format: 'json' | 'ai_output';
  payload: string;
}

class LeadImportService {
  async create(payload: LeadImportCreatePayload): Promise<LeadImportBatch> {
    return apiClient.post('/leads/import/', payload);
  }

  async createFromCsv(formData: FormData): Promise<LeadImportBatch> {
    return apiClient.postFormData('/leads/import/csv/', formData);
  }

  async parsePreview(payload: LeadImportParsePayload): Promise<{ count: number; preview: Array<Record<string, unknown>> }> {
    return apiClient.post('/leads/import/parse/', payload);
  }

  async list(params?: Record<string, string | number>): Promise<{ count: number; results: LeadImportBatch[] }> {
    return apiClient.get('/lead-imports/', params);
  }

  async get(batchId: string): Promise<LeadImportBatch> {
    return apiClient.get(`/lead-imports/${batchId}/`);
  }

  async listRecords(batchId: string, params?: Record<string, string | number>): Promise<{ count: number; results: LeadImportRecord[] }> {
    return apiClient.get(`/lead-imports/${batchId}/records/`, params);
  }

  async patchRecord(batchId: string, recordId: string, normalizedData: Record<string, unknown>): Promise<LeadImportRecord> {
    return apiClient.patch(`/lead-imports/${batchId}/records/${recordId}/`, { normalized_data: normalizedData });
  }

  async approveRecord(batchId: string, recordId: string, includeDuplicates = false): Promise<LeadImportRecord> {
    return apiClient.post(`/lead-imports/${batchId}/records/${recordId}/approve/`, { include_duplicates: includeDuplicates });
  }

  async rejectRecord(batchId: string, recordId: string): Promise<LeadImportRecord> {
    return apiClient.post(`/lead-imports/${batchId}/records/${recordId}/reject/`, {});
  }

  async bulkApprove(batchId: string, recordIds?: string[], includeDuplicates = false): Promise<{ approved: number; skipped: number }> {
    return apiClient.post(`/lead-imports/${batchId}/bulk-approve/`, {
      record_ids: recordIds || [],
      include_duplicates: includeDuplicates,
    });
  }
}

export const leadImportService = new LeadImportService();
